"use client";

import React from "react";
import { motion } from "framer-motion";
import { Calculator, Scale, Stethoscope, Wrench, ShoppingBag, Cpu, ArrowRight, CircleCheck as CheckCircle2, Sparkles, Building2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsData = [{
  id: "accountants",
  title: "Accountants & Financial Firms",
  description: "Bespoke SEO strategies designed to attract high-value business accounts, corporate tax clients, and ongoing retainer contracts across local and national search.",
  linkPath: "/B2BSeoLeadGenerationPage",
  badgeText: "B2B & Finance",
  highlight: "High-Value Retainers"
}, {
  id: "solicitors",
  title: "Solicitors & Legal Practices",
  description: "Targeted technical and geotargeted search campaigns for law firms competing for high-stakes instructions in commercial, probate, and family law.",
  linkPath: "/LocalSEOPage",
  badgeText: "Legal Services",
  highlight: "Geotargeted Authority"
}, {
  id: "dentists",
  title: "Dentists & Private Healthcare",
  description: "Patient-focused local SEO and Google Maps optimization that drives qualified inquiries for high-margin cosmetic, implant, and private treatments.",
  linkPath: "/LocalSEOPage",
  badgeText: "Healthcare",
  highlight: "Private Patient Inquiries"
}, {
  id: "trades",
  title: "Plumbers, Electricians & Trades",
  description: "Dominant local search and map pack positioning ensuring consistent inbound call volume for emergency call-outs and profitable contract installations.",
  linkPath: "/LocalSEOPage",
  badgeText: "Home Services",
  highlight: "Map Pack Dominance"
}, {
  id: "ecommerce",
  title: "Ecommerce & Retail Brands",
  description: "Custom schema markup, category page optimization, and technical site structures engineered to grow organic sales without depending entirely on paid ads.",
  linkPath: "/EcommerceSeoPage",
  badgeText: "Online Retail",
  highlight: "Scalable Organic Revenue"
}, {
  id: "tech-saas",
  title: "SaaS & B2B Technology",
  description: "Topical authority architecture and search intent mapping built to capture high-intent buyers throughout long B2B software decision cycles.",
  linkPath: "/B2BSeoLeadGenerationPage",
  badgeText: "Tech & SaaS",
  highlight: "Pipeline Generation"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Sectors() {
  const getSectorIcon = (id: string) => {
    switch (id) {
      case "accountants":
        return <Calculator className="size-6 text-primary" />;
      case "solicitors":
        return <Scale className="size-6 text-primary" />;
      case "dentists":
        return <Stethoscope className="size-6 text-primary" />;
      case "trades":
        return <Wrench className="size-6 text-primary" />;
      case "ecommerce":
        return <ShoppingBag className="size-6 text-primary" />;
      case "tech-saas":
        return <Cpu className="size-6 text-primary" />;
      default:
        return <Building2 className="size-6 text-primary" />;
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-border">
            <Sparkles className="size-3.5 text-accent mr-1" />
            Tailored Industry Expertise
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground">
            Proven SEO Results Across Key UK & Global Sectors
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Every market has unique search dynamics. When you work directly with me, I build a custom SEO framework tailored to your sector's conversion funnel, compliance requirements, and buyer intent.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {sectorsData.map((sector, i) => <motion.div key={sector.id} data-index={i} variants={itemVariants}>
              <Card className="h-full border border-border bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between overflow-hidden">
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 w-fit">
                      {getSectorIcon(sector.id)}
                    </div>
                    <Badge variant="outline" className="text-xs font-medium text-muted-foreground border-border">
                      {sector.badgeText}
                    </Badge>
                  </div>

                  <div>
                    <CardTitle className="text-xl font-bold font-serif text-foreground mb-2">
                      {sector.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {sector.description}
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-2">
                  <div className="flex items-center text-xs font-medium text-foreground/80 gap-2 bg-muted/60 px-3 py-2 rounded-md">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Focus: {sector.highlight}</span>
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="ghost" className="w-full justify-between text-primary hover:text-primary hover:bg-primary/5 group px-3">
                    <Link to={sector.linkPath}>
                      <span className="font-medium text-sm">Explore Strategy</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 md:mt-20">
          <Card className="border border-border bg-gradient-to-br from-primary/5 via-background to-secondary/30 p-8 md:p-12 text-center max-w-4xl mx-auto shadow-sm">
            <div className="space-y-4 max-w-2xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
                Don't See Your Specific Sector Listed Here?
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                I specialize in high-intent organic search campaigns for niche B2B services, specialized professions, and unique markets. You always work directly with me to craft a bespoke strategy.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm w-full sm:w-auto">
                  <Link to="/ContactPage">
                    Book a Free Consultation
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground w-full sm:w-auto">
                  <Link to="/CaseStudiesPage">
                    View Real Case Studies
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}